import type { OrderService } from './orders.js';
import type { WarehouseCountry } from './ship-to.js';
import { vendorAssetUrl } from './vendor-assets.js';

/**
 * Purchase receipts on a new order (MYP-96, rule since MYP-140).
 *
 * The vendor's answer (Q&A v2, question 8; `orders_create` revised 2026-09-29): each image is
 * uploaded with `uploadImg`, and the paths go with the order in `packageinfo`, keyed by package
 * id. The vendor keeps them in two cases only, and ignores them otherwise:
 *
 * - the order ships from Korea, Thailand, Japan or Taiwan (warehouses 4, 6, 27, 29), where they
 *   are **required** for customs — without them the order is refused with `-6`;
 * - insurance is chosen, in any warehouse.
 *
 * MyPoz's own rule (Liying, MYP-140) is the same: Korea, Taiwan, Thailand and Japan need them,
 * insurance anywhere needs them, China does not. So the app asks for receipts only where they are
 * needed and kept — a receipt collected and then silently dropped is a promise nobody keeps.
 */

export const MAX_RECEIPTS_PER_PACKAGE = 3;

/** The vendor's own list (`orders_create` `-6`): warehouses whose orders need a receipt each. */
export const RECEIPT_REQUIRED_WAREHOUSES: ReadonlySet<string> = new Set(['4', '6', '27', '29']);

/**
 * The countries whose warehouses need receipts (MYP-140), so a warehouse opened there later is
 * covered without a new id in the list above.
 */
export const RECEIPT_REQUIRED_COUNTRIES: ReadonlySet<WarehouseCountry> = new Set([
  'KR',
  'TH',
  'JP',
  'TW',
]);

/**
 * Whether a warehouse's orders need a receipt on every package. Either its id is on the vendor's
 * list or its country is — the country comes from the warehouse list, and while that is loading,
 * failed, or does not carry the warehouse (hidden ones), the vendor's id list still answers.
 */
export function receiptsRequired(
  warehouseId: string | undefined,
  country: WarehouseCountry | undefined,
): boolean {
  if (warehouseId !== undefined && RECEIPT_REQUIRED_WAREHOUSES.has(warehouseId)) return true;
  return country !== undefined && RECEIPT_REQUIRED_COUNTRIES.has(country);
}

/**
 * Whether a service is insurance. By its name in either language: every warehouse has its own
 * insurance service id (13 in Guangzhou; 21, 27–32, 42 and 48 elsewhere), so no id would do.
 */
export function isInsuranceService(service: Pick<OrderService, 'name' | 'nameNative'>): boolean {
  return [service.name, service.nameNative ?? ''].some((name) => /insurance|保险/i.test(name));
}

/** Whether the chosen services include insurance. An id the catalog does not know is not. */
export function insuranceChosen(
  serviceIds: readonly string[],
  services: readonly Pick<OrderService, 'id' | 'name' | 'nameNative'>[],
): boolean {
  return serviceIds.some((id) => {
    const service = services.find((item) => item.id === id);
    return service !== undefined && isInsuranceService(service);
  });
}

/** Whether an order's packages each need a receipt: a warehouse that needs them, or insurance. */
export function receiptsWanted(
  warehouseId: string | undefined,
  country: WarehouseCountry | undefined,
  insured: boolean,
): boolean {
  return insured || receiptsRequired(warehouseId, country);
}

/** One ticked package, as far as receipts care: its id and the warehouse it is in. */
export interface ReceiptHoldPackage {
  readonly id: string;
  /** Undefined while the package's details are still being looked up. */
  readonly warehouseId?: string;
}

/** What Create order needs to know to decide where receipts hold it (MYP-140). */
export interface ReceiptHoldInput {
  /** Every ticked package. */
  readonly packages: readonly ReceiptHoldPackage[];
  /** A warehouse's country, when the warehouse list knows it. */
  readonly countryOf: (warehouseId: string | undefined) => WarehouseCountry | undefined;
  /** Insurance is among the chosen order services. */
  readonly insured: boolean;
  /** Uploaded receipt paths, by package id. */
  readonly paths: Readonly<Record<string, readonly string[]>>;
  /** Packages with an upload still in flight. */
  readonly uploadingIds: ReadonlySet<string>;
}

export interface ReceiptHold {
  /** The ticked packages that need a receipt on this order. */
  readonly wantedIds: readonly string[];
  /** How many of them have none uploaded yet. */
  readonly missing: number;
  /**
   * Step 1 cannot go on: a package from a warehouse that needs receipts has none (the vendor
   * refuses the order with -6), or an upload is still in flight — a half-sent receipt is never
   * left out of the order.
   */
  readonly step1Blocked: boolean;
  /**
   * Step 3 (delivery and services) cannot go on: receipts are still missing. Once Step 1 is clear
   * these can only be insurance's, which is chosen on Step 3 — so that is where they hold.
   */
  readonly step3Blocked: boolean;
  /** Why receipts are asked for, for the hint under the list; undefined when none are. */
  readonly reason?: 'warehouse' | 'insurance';
}

/**
 * Where receipts hold Create order, and why (MYP-140), as one rule:
 * - a package from a Korea/Thailand/Japan/Taiwan warehouse needs one, and holds Step 1;
 * - insurance makes every package need one, and holds Step 3 (where insurance is chosen);
 * - an upload in flight holds Step 1 whatever the rule;
 * - anything else (China, Malaysia, no insurance) asks for nothing.
 */
export function receiptHold(input: ReceiptHoldInput): ReceiptHold {
  const { packages, countryOf, insured, paths, uploadingIds } = input;
  const requiredIds = packages
    .filter((item) => receiptsRequired(item.warehouseId, countryOf(item.warehouseId)))
    .map((item) => item.id);
  const wantedIds = packages
    .filter((item) => receiptsWanted(item.warehouseId, countryOf(item.warehouseId), insured))
    .map((item) => item.id);

  const missingRequired = packagesMissingReceipts(requiredIds, paths).length;
  const missing = packagesMissingReceipts(wantedIds, paths).length;
  const uploading = packages.some((item) => uploadingIds.has(item.id));

  return {
    wantedIds,
    missing,
    step1Blocked: missingRequired > 0 || uploading,
    step3Blocked: missing > 0,
    reason: requiredIds.length > 0 ? 'warehouse' : wantedIds.length > 0 ? 'insurance' : undefined,
  };
}

/**
 * The largest file a customer may pick: 20 MB. Photos are shrunk in the browser before upload
 * (`fitWithin`, `RECEIPT_MAX_EDGE`), so this only guards against decoding something enormous.
 */
export const RECEIPT_PICK_MAX_BYTES = 20 * 1024 * 1024;

/**
 * The largest image actually sent, after shrinking: 5 MB (about 6.7 MB once base64-encoded). A
 * shrunk receipt is far below this; it only refuses an image that would not shrink.
 */
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;

/** Longest side, in pixels, a receipt is shrunk to before upload — still readable as a receipt. */
export const RECEIPT_MAX_EDGE = 2000;

export const RECEIPT_TYPES: readonly string[] = ['image/jpeg', 'image/png'];

/** Why a picked file is refused before anything is uploaded. */
export type ReceiptFileProblem = 'type' | 'size';

export function checkReceiptFile(file: {
  readonly type: string;
  readonly size: number;
}): ReceiptFileProblem | undefined {
  if (!RECEIPT_TYPES.includes(file.type.toLowerCase())) return 'type';
  if (file.size <= 0 || file.size > RECEIPT_PICK_MAX_BYTES) return 'size';
  return undefined;
}

/**
 * The size to draw an image at so its longest side is at most `maxEdge`, keeping its proportions.
 * An image already within it keeps its size (never enlarged).
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { readonly width: number; readonly height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest <= 0) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** The selected packages that have no uploaded receipt yet. */
export function packagesMissingReceipts(
  packageIds: readonly string[],
  paths: Readonly<Record<string, readonly string[]>>,
): readonly string[] {
  return packageIds.filter((id) => (paths[id]?.length ?? 0) === 0);
}

/**
 * `packageinfo` for `orders_create`: the vendor's recommended format, `{"<pkgId>": [paths]}`.
 * Only the order's own packages, only ones with at least one path, at most three each. Undefined
 * when there is nothing to send. (URL-encoding is the form encoder's job.)
 */
export function packageInfoParam(
  packageIds: readonly string[],
  paths: Readonly<Record<string, readonly string[]>>,
): string | undefined {
  const entries: [string, string[]][] = [];
  for (const id of packageIds) {
    const list = (paths[id] ?? []).filter((path) => path.trim() !== '');
    if (list.length > 0) entries.push([id, list.slice(0, MAX_RECEIPTS_PER_PACKAGE)]);
  }
  return entries.length === 0 ? undefined : JSON.stringify(Object.fromEntries(entries));
}

/** The full path `uploadImg` stored the image at: `imgUrl` + `imgName`, or undefined. */
export function uploadedImagePath(reply: unknown): string | undefined {
  if (typeof reply !== 'object' || reply === null) return undefined;
  const body = reply as Readonly<Record<string, unknown>>;
  const dir = typeof body['imgUrl'] === 'string' ? body['imgUrl'].trim() : '';
  const name = typeof body['imgName'] === 'string' ? body['imgName'].trim() : '';
  if (dir === '' || name === '') return undefined;
  return dir.endsWith('/') ? `${dir}${name}` : `${dir}/${name}`;
}

/** The receipt paths on an order's package (`pkgInfo[].imageurl`, an array; `[""]` when none). */
export function receiptPathsFrom(value: unknown): readonly string[] {
  const list = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  return list
    .filter((item): item is string => typeof item === 'string')
    .flatMap((item) => item.split(','))
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

/** A stored receipt path as a link on the API's own host — see `vendorAssetUrl`. */
export function receiptImageUrl(path: string, origin: string): string {
  return vendorAssetUrl(path, origin);
}
